import React from "react";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Award, MapPin } from "lucide-react";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_senior-balance-rehab/artifacts/3rtnhgbe_ChatGPT%20Image%20Jun%2027%2C%202026%2C%2008_44_41%20AM.png";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 px-6 md:px-12 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-20 -left-20 w-96 h-96 rounded-full bg-brand-terracotta/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-brand-teal/10 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7 space-y-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-sand border border-brand-clay">
            <span className="w-2 h-2 rounded-full bg-brand-terracotta" />
            <span className="text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-brand-terracotta" data-testid="hero-eyebrow">
              Fall and Frailty Prevention Clinic · Reading Physio
            </span>
          </div>

          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] tracking-tight text-brand-blue"
            data-testid="hero-headline"
          >
            Regain confidence in every step you take.
          </h1>

          <p className="text-lg md:text-xl text-slate-700 leading-relaxed max-w-2xl" data-testid="hero-description">
            Regain complete confidence in your movement, reclaim lost balance, and prevent future falls.
            We deliver expert, clinical strength and mobility training tailored for seniors and individuals
            recovering from injuries in Reading and Berkshire, supervised entirely by chartered physiotherapists.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 pt-2">
            <Button
              onClick={() => scrollTo("contact")}
              className="bg-brand-blue hover:bg-brand-blue/90 text-white rounded-full px-8 h-14 text-base md:text-lg font-semibold shadow-md"
              data-testid="hero-primary-cta"
            >
              Book Assessment
            </Button>
            <Button
              onClick={() => scrollTo("services")}
              variant="outline"
              className="rounded-full px-8 h-14 text-base md:text-lg font-semibold border-2 border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white"
              data-testid="hero-secondary-cta"
            >
              Explore Services
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-8">
            <div className="flex items-start gap-3" data-testid="hero-trust-1">
              <ShieldCheck size={24} strokeWidth={1.5} className="text-brand-teal shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-brand-blue">Chartered Physios</p>
                <p className="text-sm text-slate-600">HCPC & CSP registered</p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-testid="hero-trust-2">
              <Award size={24} strokeWidth={1.5} className="text-brand-teal shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-brand-blue">Evidence-Based</p>
                <p className="text-sm text-slate-600">Clinical methodology</p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-testid="hero-trust-3">
              <MapPin size={24} strokeWidth={1.5} className="text-brand-teal shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-brand-blue">Reading & Berkshire</p>
                <p className="text-sm text-slate-600">Local, accessible care</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl">
            <img
              src={HERO_IMAGE}
              alt="Senior receiving compassionate physiotherapy"
              className="w-full h-[440px] md:h-[560px] object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-brand-blue/85 to-transparent">
              <p className="text-white font-display text-xl md:text-2xl leading-snug">
                &ldquo;Movement is the medicine for healthy aging.&rdquo;
              </p>
            </div>
          </div>

          <div className="hidden md:block absolute -top-6 -right-6 bg-white rounded-2xl shadow-xl p-5 border border-brand-clay max-w-[240px]">
            <div className="flex items-baseline gap-2">
              <span className="font-display text-4xl text-brand-blue font-bold">15+</span>
              <span className="text-sm text-slate-600">years</span>
            </div>
            <p className="text-sm text-slate-700 mt-1 leading-tight">
              Supporting Berkshire seniors stay strong and independent.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
